import { TICKET_MIN_PAID_PRICE_CENTS } from '@pulsify/shared'
import type { TicketType } from '@pulsify/shared/dto'
import type { TicketTypeInput } from '~/entities/ticket/api/tickets-api'
import { centsToInput, parseEuros } from './ticket-format'

const MAX_PRICE_CENTS = 1_000_000
const MAX_SEATS = 100_000

export type TicketTypeDraft = {
  name: string
  price: string
  seats: string
  isStudent: boolean
  doorPrice: string
  active: boolean
}

export type TicketTypeIssue =
  | 'name'
  | 'price'
  | 'priceTooLow'
  | 'seats'
  | 'seatsBelowSold'
  | 'doorPrice'

export function draftFromType(type: TicketType | null, locale?: string): TicketTypeDraft {
  return {
    name: type?.name ?? '',
    price: type ? centsToInput(type.priceCents, locale) : '',
    seats: type?.quantity ? String(type.quantity) : '',
    isStudent: type?.isStudent ?? false,
    doorPrice: type?.doorPriceCents ? centsToInput(type.doorPriceCents, locale) : '',
    active: type?.active ?? true,
  }
}

export function draftPriceCents(draft: TicketTypeDraft): number {
  return parseEuros(draft.price) ?? 0
}

function parseSeats(text: string): number | null | undefined {
  const trimmed = text.trim()
  if (trimmed === '') return null
  if (!/^\d+$/.test(trimmed)) return undefined
  const seats = Number(trimmed)
  return seats >= 1 && seats <= MAX_SEATS ? seats : undefined
}

function parseAmount(text: string): number | null {
  const cents = parseEuros(text)
  return cents !== null && cents <= MAX_PRICE_CENTS ? cents : null
}

export function inputFromDraft(
  draft: TicketTypeDraft,
  soldCount = 0,
): { ok: true; input: TicketTypeInput } | { ok: false; issue: TicketTypeIssue } {
  const name = draft.name.trim()
  if (!name) return { ok: false, issue: 'name' }
  const priceCents = parseAmount(draft.price)
  if (priceCents === null) return { ok: false, issue: 'price' }
  if (priceCents > 0 && priceCents < TICKET_MIN_PAID_PRICE_CENTS) {
    return { ok: false, issue: 'priceTooLow' }
  }
  const quantity = parseSeats(draft.seats)
  if (quantity === undefined) return { ok: false, issue: 'seats' }
  if (quantity !== null && quantity < soldCount) return { ok: false, issue: 'seatsBelowSold' }
  const doorCents = priceCents === 0 ? parseAmount(draft.doorPrice) : 0
  if (doorCents === null) return { ok: false, issue: 'doorPrice' }
  return {
    ok: true,
    input: {
      name,
      priceCents,
      quantity,
      isStudent: draft.isStudent,
      doorPriceCents: doorCents > 0 ? doorCents : null,
      active: draft.active,
    },
  }
}
